import { Helmet } from 'react-helmet-async';

const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_479182d4-7fde-4a87-91a5-42a6ac4fac5e/artifacts/an0m2tpw_WhatsApp%20Image%202026-02-04%20at%2017.35.14%20%286%29.jpeg';
const SITE_NAME = 'CEPRES International University';
const BASE_URL = 'https://ciu.edu.lr';

export const SEO = ({ 
  title, 
  description, 
  image = DEFAULT_IMAGE, 
  url = '', 
  type = 'website',
  keywords = ''
}) => {
  const fullTitle = title 
    ? `${title} | ${SITE_NAME} | Accredited Online University Africa` 
    : `${SITE_NAME} | #1 Accredited Online University in Africa | Study from USA, Europe & Worldwide`;
  
  const fullUrl = url ? `${BASE_URL}${url}` : BASE_URL;
  
  const defaultDescription = 'CEPRES International University - Africa\'s leading accredited online university. Study from anywhere: USA, Europe, Asia, or Africa. Flexible online degree programs in Healthcare, Public Health, Agriculture & Education. Internationally recognized. Apply now!';
  
  const defaultKeywords = 'online university Africa, accredited online university, online schools Africa, online schools Europe, online schools United States, online schools USA, universities in Africa, African university online, international online university, accredited online degree, distance learning Africa, online healthcare degree, online public health degree, study online from USA, study online from Europe, best online university Africa, affordable online university, CEPRES, CIU, Liberia, West Africa, flexible online degree, accredited African university';

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || defaultDescription} />
      <meta name="twitter:image" content={image} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
    </Helmet>
  );
};

// Page-specific SEO configurations - Optimized for international search
export const pageSEO = {
  home: {
    title: null, // Uses default
    description: 'CEPRES International University - Africa\'s #1 accredited online university. Study from USA, Europe, Asia or anywhere! Flexible online degrees in Healthcare, Public Health & Agriculture. Affordable tuition, internationally recognized. Join 1000+ students worldwide!',
    keywords: 'online university Africa, accredited online university, online schools Africa, online schools Europe, online schools USA, universities in Africa, best online university, affordable online degree, study online internationally, distance learning, healthcare degree online, public health online'
  },
  about: {
    title: 'About Us - Leading Online University in Africa',
    description: 'Learn about CEPRES International University - Africa\'s premier accredited online university. Founded by Dr. Mogana S. Flomo with 28+ years experience. Serving students from USA, Europe, Asia & Africa since 2014.',
    keywords: 'about CEPRES, African online university, Dr Mogana Flomo, accredited university Africa, international online education, best African university'
  },
  programs: {
    title: 'Online Degree Programs - Study from USA, Europe or Anywhere',
    description: 'Explore 19 accredited online degree programs at CEPRES. Bachelor\'s in Public Health, Community Health, Agriculture & more. Study from USA, Europe, Asia or Africa. Flexible 2, 3 or 4-year tracks. Internationally recognized degrees.',
    keywords: 'online degree programs, accredited online degrees, bachelor degree online, public health online degree, study online from USA, study online from Europe, African university degrees, healthcare degree, agriculture degree online'
  },
  vocational: {
    title: 'Online Vocational & Certificate Programs',
    description: 'Fast-track your career with accredited online certificates and diplomas. Study from USA, Europe or anywhere. Healthcare, IT, Business & Agriculture programs. Graduate in 6-12 months.',
    keywords: 'online certificate programs, vocational training online, diploma programs online, professional certification, career training online, study online internationally'
  },
  admissions: {
    title: 'Admissions - Apply Online from USA, Europe or Worldwide',
    description: 'Apply to CEPRES International University from anywhere in the world. Simple online application for students in USA, Europe, Asia, Africa & beyond. Affordable tuition, flexible payment. January & May 2026 intakes open!',
    keywords: 'university admission online, apply to African university, international student admission, online university application, study in Africa from USA, study in Africa from Europe'
  },
  apply: {
    title: 'Apply Now - Online Application for International Students',
    description: 'Start your application to Africa\'s #1 accredited online university. Students from USA, Europe, Asia & worldwide welcome. Simple 5-minute online process. 19 degree programs, flexible schedules.',
    keywords: 'apply online university, international student application, online degree application, study online Africa, enroll online university'
  },
  contact: {
    title: 'Contact Us - International Student Support',
    description: 'Contact CEPRES International University. Support for students in USA, Europe, Asia, Africa & worldwide. Email, phone & online chat available. Admissions inquiries welcome.',
    keywords: 'contact CEPRES, university contact, international student support, admissions contact, online university help'
  },
  blog: {
    title: 'News & Updates - Online Education Insights',
    description: 'Latest news from Africa\'s leading online university. Education insights, student success stories, campus updates. Follow our journey in transforming online education globally.',
    keywords: 'university news, online education blog, African university news, student success stories, education updates'
  },
  stories: {
    title: 'Student Success Stories - Global Alumni Network',
    description: 'Read inspiring stories from CEPRES graduates worldwide. Students from USA, Europe, Africa share their online learning journey. See how our accredited degrees transform careers.',
    keywords: 'student stories, alumni success, online learning testimonials, graduate stories, international students'
  },
  services: {
    title: 'Student Services - Online Learning Support',
    description: 'Comprehensive support for online students worldwide. Digital library, virtual labs, career services, academic advising. 24/7 support for students in USA, Europe, Asia & Africa.',
    keywords: 'online student services, digital learning support, virtual campus, online library, career services'
  },
  accreditation: {
    title: 'Accreditation - Internationally Recognized Degrees',
    description: 'CEPRES is fully accredited by the National Commission on Higher Education. Our degrees are internationally recognized in USA, Europe, Asia & Africa. Quality assurance guaranteed.',
    keywords: 'university accreditation, accredited online university, recognized degrees, international accreditation, quality education, NCHE accreditation'
  },
  research: {
    title: 'Research & Innovation - Global Health Research',
    description: 'Join CEPRES\'s research community. Applied research in public health, agriculture & community development. International research collaborations with partners in USA, Europe & Africa.',
    keywords: 'university research, health research Africa, agricultural research, international research, academic research online'
  },
  whyCIU: {
    title: 'Why Choose CEPRES - Best Online University Value',
    description: 'Why students from USA, Europe & worldwide choose CEPRES. Affordable accredited degrees, flexible online schedules, international recognition, strong career outcomes. #1 value in online education.',
    keywords: 'why choose CEPRES, best online university, affordable online degree, online education value, international online university benefits'
  },
  admin: {
    title: 'Admin Dashboard',
    description: 'CEPRES Administration Dashboard',
    keywords: ''
  }
};
